import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { ConductorMaterial } from '../fields/ConductorMaterial';
import { ConductorFlexibility } from '../fields/ConductorFlexibility';
import { InsulationMaterial } from '../fields/InsulationMaterial';
import { Temperature } from '../fields/Temperature';
import { Voltage } from '../fields/Voltage';
import { WireSize } from '../fields/WireSize';
import { CPRValidator } from '../fields/CPR';
import { ResistancePerUnitLength } from '../fields/ResistancePerUnitLength';
import { InductancePerUnitLength } from '../fields/InductancePerUnitLength';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { ConnectionCores } from '../../types/ConnectionCores';
import { CableScreen } from '../fields/CableScreen';
import { CableUse } from '../fields/CableUse';
import { voltageTypes } from '../../types/VoltageType';
import { AbstractPort, Port } from '../fields/AbstractPort';

const port = AbstractPort.extend({
    AC: Port.AC.default({}),
    DC: Port.DC.default({}),
}).default({
    powerFlowDirection: 'bidirectional',
});

export const CableLinesValidator = z.object({
    AC: z
        .object({
            L1: z.boolean().default(true),
            L2: z.boolean().default(true),
            L3: z.boolean().default(true),
            N: z.boolean().default(true),
            PE: z.boolean().default(false),
        })
        .default({}),
    DC: z
        .object({
            'L+': z.boolean().default(true),
            'M': z.boolean().default(false),
            'L-': z.boolean().default(true),
            'PE': z.boolean().default(true),
        })
        .default({}),
});

const CableBase = z.object({
    type: z.literal('cable').default('cable'),
    electrical: z
        .object({
            // TODO: refactor code where ports can be undefined
            ports: z.array(port).default([undefined]),

            wireSize: WireSize.validator,

            cores: z.nativeEnum(ConnectionCores).default(ConnectionCores.SINGLE),

            operatingVoltage: z
                .object({
                    toEarth: Voltage.validators.value,
                    betweenLines: Voltage.validators.value,
                })
                .default({
                    toEarth: {},
                    betweenLines: {},
                }),

            numberOfConductors: z.number().int().min(1).max(4).nullable().default(null),
            hasPE: z.boolean().default(false),
            voltageTypes: z.array(z.enum(voltageTypes)).default([]),

            resistancePerLength: ResistancePerUnitLength.validator,
            inductancePerLength: InductancePerUnitLength.validator,

            cpr: CPRValidator,
            intertrippingWireSize: z.number().nullable().default(null), // in mm2
        })
        .default({}),

    mechanical: z
        .object({
            conductorMaterial: ConductorMaterial.validator.default('copper'),
            conductorFlexibility: ConductorFlexibility.validator.nullable().default(null),
            // // TODO: insulation => conductor temp => remove conductor temp from connection, replace with insulation
            insulationMaterial: InsulationMaterial.validator.default('PVC'),
            screen: CableScreen.validator.nullable().default(null),
        })
        .default({}),

    environmental: z
        .object({
            installationTemperature: Temperature.validator,
            use: CableUse.validator.default([]),
        })
        .default({}),

    performance: z.object({}).default({}),
    communication: z.object({}).default({}),
});

export const CableValidator = ComponentValidator.omit({
    performance: true,
    communication: true,
    mechanical: true,
    environmental: true,
}).merge(CableBase);
export const CablePayloadValidator = ComponentPayloadValidator.merge(CableBase);

export const CableDefinition: ComponentDefinition = {
    type: 'cable',
    name: 'Cable',
    plural: 'Cables',
    indicator: 'WIRE',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: true,
    disableDiagramUse: true,
    ports: {
        min: 0,
        max: 0,
        powerFlowDirections: [],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: CableValidator,
        payload: CablePayloadValidator,
    },
};

export type Cable = z.infer<typeof CableValidator>;
export type CablePayload = z.infer<typeof CablePayloadValidator>;
export type CableLines = z.infer<typeof CableLinesValidator>;
